import React from 'react';
import { RefreshCw, MoreVertical, Clock, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDateTime, formatTime } from '../utils/dateFormatter';

const EventCard = ({ event, isNext = false, onMenuClick }) => {
  const navigate = useNavigate();
  const isPast = new Date(event.startTime) < new Date();
  
  // Get event type styling
  const getEventTypeStyle = (type) => {
    const styles = {
      rehearsal: 'bg-green-100 text-green-800 border-green-200',
      concert: 'bg-orange-100 text-orange-800 border-orange-200',
      recording: 'bg-purple-100 text-purple-800 border-purple-200',
      meeting: 'bg-blue-100 text-blue-800 border-blue-200',
      audition: 'bg-red-100 text-red-800 border-red-200',
      default: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return styles[type] || styles.default;
  };
  
  // Get confirmed/declined/maybe attendees
  const getAttendeesByStatus = (status) => {
    const attendees = event.attendees?.filter(a => a.status === status).map(a => a.user?.username || a.user?.email || 'Unknown') || [];
    const publicRsvps = event.publicRsvps?.filter(r => r.status === status).map(r => r.name) || [];
    return [...attendees, ...publicRsvps];
  };
  
  const confirmedNames = getAttendeesByStatus('confirmed');
  const declinedNames = getAttendeesByStatus('declined');
  const maybeNames = getAttendeesByStatus('maybe');
  
  return (
    <div 
      className={`border rounded-md p-3 cursor-pointer transition-colors ${
        isNext 
          ? 'border-primary bg-primary/5 hover:bg-primary/10' 
          : 'border-border bg-card hover:bg-card/80'
      } ${isPast ? 'opacity-60' : ''}`}
      onClick={() => navigate(`/event/${event._id}`)}
    >
      {/* Header row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 flex-1">
          <h3 className="font-semibold">{event.title}</h3>
          {(event.recurring || event.isRecurringInstance) && (
            <RefreshCw className="w-4 h-4 text-muted-foreground" title="Recurring event" />
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded border ${getEventTypeStyle(event.type)}`}>
            {event.type}
          </span>
          {onMenuClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick(event);
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      {/* Date/Time/Location row */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
        <div className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          <span>{formatDateTime(event.startTime)} - {formatTime(event.endTime)}</span>
        </div>
        {event.location?.name && (
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>{event.location.name}</span>
          </div>
        )}
      </div>
      
      {/* Band and Setlist row */}
      <div className="flex items-center gap-3 text-sm mb-2">
        {event.band?.name && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/band/${event.band._id}`);
            }}
            className="text-muted-foreground hover:text-primary hover:underline"
          >
            Band: {event.band.name}
          </button>
        )}
        {event.setlist && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/setlist/${event.setlist._id}`);
            }}
            className="text-primary hover:text-primary/80 hover:underline"
          >
            Setlist: {event.setlist.name || 'View Setlist'}
            {event.inheritedSetlist && (
              <span className="ml-1 text-xs text-gray-500">(default)</span>
            )}
            {event.isRecurringInstance && event.hasOwnSetlist && (
              <span className="ml-1 text-xs text-green-600">(custom)</span>
            )}
          </button>
        )}
      </div>
      
      {/* RSVP details */}
      {(confirmedNames.length > 0 || declinedNames.length > 0 || maybeNames.length > 0) && (
        <div className="space-y-1 text-xs pt-2 border-t border-border">
          {confirmedNames.length > 0 && (
            <div className="flex">
              <span className="text-green-600 mr-2">Going:</span>
              <span className="text-muted-foreground">{confirmedNames.join(', ')}</span>
            </div>
          )}
          {declinedNames.length > 0 && (
            <div className="flex">
              <span className="text-red-600 mr-2">Not going:</span>
              <span className="text-muted-foreground">{declinedNames.join(', ')}</span>
            </div>
          )}
          {maybeNames.length > 0 && (
            <div className="flex">
              <span className="text-yellow-600 mr-2">Maybe:</span>
              <span className="text-muted-foreground">{maybeNames.join(', ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventCard;